import { Close, UserIconFilled } from '@/components/icons'
import { LIMIT, OFFSET } from '@/constants/Common'
import { APP_CUSTOM_ERROR } from '@/constants/MessageConstants'
import { ROUTE_PLAYER_PROFILE } from '@/constants/Routes'
import { useAppSelector } from '@/hooks/useRedux'
import { authSelector } from '@/pages/auth/selectors'
import { userSelector } from '@/pages/user/selectors'
import { useFollow, useGetFollowers, useGetFollowings } from '@/services'
import { Follower, FollowingInfo } from '@/types/Activity'
import { Dialog, Transition } from '@headlessui/react'
import { Fragment, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

type Props = {
  open: 'FOLLOWING' | 'FOLLOWERS' | ''
  onClose: () => void
  playerId?: number
  followingInfo?: FollowingInfo
  refetchFollowingInfo?: () => void
}

export const FollowingModal = ({ open, onClose, playerId, followingInfo, refetchFollowingInfo }: Props) => {
  const { t } = useTranslation()

  const { token } = useAppSelector(authSelector)
  const { user } = useAppSelector(userSelector)

  const [tab, setTab] = useState<'FOLLOWING' | 'FOLLOWERS' | ''>('')
  const [offset, setOffset] = useState(OFFSET)
  const [list, setList] = useState<Follower[]>([])
  const [lastElement, setLastElement] = useState<any>(null)

  const { data: resFollowings, isLoading: isLoadingFollowings } = useGetFollowings(
    playerId,
    LIMIT,
    offset,
    token,
    tab === 'FOLLOWING'
  )
  const { data: resFollowers, isLoading: isLoadingFollowers } = useGetFollowers(
    playerId,
    LIMIT,
    offset,
    token,
    tab === 'FOLLOWERS'
  )
  const { mutate: follow } = useFollow()

  const observer = useRef(
    new IntersectionObserver((entries) => {
      const first = entries[0]
      if (first.isIntersecting) {
        setOffset((pre) => pre + LIMIT)
      }
    })
  )

  const onChangeTab = (value: 'FOLLOWING' | 'FOLLOWERS') => {
    if (tab !== value) {
      setList([])
      setOffset(OFFSET)
      setTab(value)
    }
  }

  const onFollow = (value: Follower) => {
    if (!value.isFollow)
      follow(value.id, {
        onSuccess: () => {
          if (refetchFollowingInfo) {
            refetchFollowingInfo()
          }
          const index = list.findIndex((item) => item.id === value.id)
          list[index] = {
            ...list[index],
            isFollow: true,
          }
          toast.success(t('common.success'))
        },
        onError: (error: any) => {
          toast.error(error.response?.data.message || APP_CUSTOM_ERROR)
        },
      })
  }

  useEffect(() => {
    setTab(open)
    setList([])
    setOffset(OFFSET)
  }, [open])

  useEffect(() => {
    if (resFollowers) {
      const newFollowers = resFollowers.results.filter((follower) => !list.some((item) => item.id === follower.id))
      setList((prevList) => [...prevList, ...newFollowers])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resFollowers])

  useEffect(() => {
    if (resFollowings) {
      const newFollowings = resFollowings.results.filter((follower) => !list.some((item) => item.id === follower.id))
      setList((prevList) => [...prevList, ...newFollowings])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resFollowings])

  useEffect(() => {
    const currentElement = lastElement
    const currentObserver = observer.current

    if (currentElement) {
      currentObserver.observe(currentElement)
    }

    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement)
      }
    }
  }, [lastElement])

  return (
    <Transition appear show={!!open} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg h-[512px] transform overflow-hidden rounded-2xl bg-white px-6 py-4 text-left align-middle shadow-xl transition-all relative">
                <button
                  className="w-9 h-9 rounded-full bg-navy-900 bg-opacity-10 flex items-center justify-center hover:bg-opacity-20 absolute left-2 top-3"
                  onClick={onClose}
                >
                  <Close className="text-navy-900" />
                </button>
                <Dialog.Title as="h3" className="text-xl text-navy-900 font-semibold text-center">
                  {t('common.following')}
                </Dialog.Title>

                <div className="mt-6 h-full">
                  <div className="flex">
                    <button
                      className={`flex flex-1 items-center justify-center text-sm font-semibold py-2 border-b-2 transition-all duration-300 ease-in-out ${
                        tab === 'FOLLOWING'
                          ? 'border-primary text-primary'
                          : 'border-transparent text-navy-900 text-opacity-50'
                      }`}
                      onClick={() => onChangeTab('FOLLOWING')}
                    >
                      {followingInfo?.followings ? followingInfo?.followings : ''} {t('common.following')}
                    </button>
                    <button
                      className={`flex flex-1 items-center justify-center text-sm font-semibold py-2 border-b-2 transition-all duration-300 ease-in-out ${
                        tab === 'FOLLOWERS'
                          ? 'border-primary text-primary'
                          : 'border-transparent text-navy-900 text-opacity-50'
                      }`}
                      onClick={() => onChangeTab('FOLLOWERS')}
                    >
                      {followingInfo?.followers ? followingInfo?.followers : ''} {t('common.followers')}
                    </button>
                  </div>
                  <div className="mt-4 overflow-y-auto overflow-x-hidden h-[calc(100%-90px)] pr-4">
                    {((tab === 'FOLLOWERS' && !isLoadingFollowers && !resFollowers?.results?.length) ||
                      (tab === 'FOLLOWING' && !isLoadingFollowings && !resFollowings?.results?.length)) &&
                      !list.length && (
                        <p className="text-navy-900 text-opacity-70 font-semibold text-sm mt-36 text-center">
                          {tab === 'FOLLOWERS' ? t('common.noFollower') : t('common.noFollowing')}
                        </p>
                      )}
                    {list.map((item, index) => (
                      <div
                        className="flex justify-between items-center"
                        key={item.id}
                        ref={
                          index === list.length - 1 &&
                          (tab === 'FOLLOWERS' ? resFollowers?.results?.length : resFollowings?.results?.length)
                            ? setLastElement
                            : null
                        }
                      >
                        <Link
                          className="flex items-center gap-x-2 py-2 cursor-pointer"
                          to={`${ROUTE_PLAYER_PROFILE}/${item?.id}`}
                        >
                          {item.profileImage ? (
                            <img className="w-12 h-12 rounded-full" src={item.profileImage} alt="Avatar" />
                          ) : (
                            <div>
                              <UserIconFilled className="w-12 h-12 text-primary-500" />
                            </div>
                          )}
                          <span className="text-sm font-semibold text-navy-900">{item.name}</span>
                        </Link>
                        {item?.id !== user?.id && (
                          <button
                            className={`text-sm ${
                              !item.isFollow ? 'text-white bg-primary' : 'bg-navy-900 text-navy-900 bg-opacity-[0.08]'
                            } px-4 py-[6px] rounded-full font-semibold`}
                            onClick={() => onFollow(item)}
                            disabled={item?.isFollow}
                          >
                            {!item.isFollow ? t('common.follow') : t('common.following')}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
